// filepath: apps/web/src/features/admin/userRoles/UserRolesPage.tsx
//
// FR-UI-ADM-5 — page composition for the user-role admin editor.
//
// Mirrors the FR-UI-ADM-6 WebhookEndpointsPage pattern:
//   - 5 fetch states (loading / success / error_fetch /
//     error_permission_denied / error_stale_version) per FR-UI-CORE-10
//   - 5-second auto-dismiss "Takes effect on next login" notice
//     (FR-UI-ADM-5 AC-6)
//   - Sync retry button + sync-status banner
//   - Orphan panel trigger

import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/definitions/types";
import { useUsers, useSyncRefresh, useOrphanedUsers } from "./queries";
import type { KeycloakUser, UserRoleFetchError } from "./types";
import { UserRolesTable } from "./UserRolesTable";
import { SyncStatusBanner } from "./SyncStatusBanner";
import { SyncRetryButton } from "./SyncRetryButton";
import { OrphanPanel } from "./OrphanPanel";
import { BulkAssignPopover } from "./BulkAssignPopover";

interface UserRolesPageProps {
  /** Resolved environment kind — already gated to "staging" by the route. */
  environmentKind: string;
}

export function UserRolesPage({
  environmentKind,
}: UserRolesPageProps): React.ReactElement {
  const { t } = useTranslation();
  const query = useUsers();
  const syncRefresh = useSyncRefresh();
  const orphans = useOrphanedUsers();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [orphanPanelOpen, setOrphanPanelOpen] = useState(false);
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  const onSelectionChange = useCallback((id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const onBulkClear = useCallback(() => setSelected(new Set()), []);

  const onSyncRefresh = useCallback(async () => {
    await syncRefresh.mutateAsync();
  }, [syncRefresh]);

  // --- 5-state render (FR-UI-CORE-10) ---

  const errorSurface = useMemo(() => {
    if (query.error) {
      const err = query.error as ApiError & {
        kind?: UserRoleFetchError["kind"];
      };
      const kind = err.kind ?? classifyKindFromStatus(err.status);
      switch (kind) {
        case "error_permission_denied":
          return (
            <div role="alert" data-state="permission_denied">
              {t("admin.users.error.permission_denied")}
            </div>
          );
        case "error_stale_version":
          return (
            <div role="alert" data-state="stale_version">
              {t("admin.users.error.stale_version")}
            </div>
          );
        case "error_fetch":
        default:
          return (
            <div role="alert" data-state="fetch_failure">
              {t("admin.users.error.fetch_failure")}
            </div>
          );
      }
    }
    return null;
  }, [query.error, t]);

  if (query.isLoading) {
    return (
      <div data-state="loading" aria-busy="true">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} data-skeleton="row" />
        ))}
      </div>
    );
  }

  if (errorSurface) return errorSurface;

  const data = query.data;
  if (!data) {
    return (
      <div role="alert" data-state="fetch_failure">
        {t("admin.users.error.fetch_failure")}
      </div>
    );
  }

  const users: KeycloakUser[] = data.users;
  const version = data.version;
  const syncPending = data.sync_pending;
  const syncStatus = data.sync_status;

  return (
    <div data-environment-kind={environmentKind}>
      {syncStatus !== "ok" || syncPending ? (
        <SyncStatusBanner
          syncStatus={syncStatus}
          onRefresh={onSyncRefresh}
          refreshing={syncRefresh.isPending}
        />
      ) : null}

      <h1>{t("admin.users.title")}</h1>
      <p>{t("admin.users.subtitle")}</p>

      <UserRolesTable
        users={users}
        selected={selected}
        onSelectionChange={onSelectionChange}
        currentVersion={version}
        data-testid="user-roles-table"
      />

      {selected.size >= 2 ? (
        <BulkAssignPopover
          userIds={Array.from(selected)}
          currentVersion={version}
          onComplete={onBulkClear}
        />
      ) : null}

      {orphans.length > 0 ? (
        <button
          type="button"
          onClick={() => setOrphanPanelOpen((v) => !v)}
          data-state="orphan-panel-toggle"
        >
          {t("admin.users.orphan_panel.toggle", { count: orphans.length })}
        </button>
      ) : null}

      {orphanPanelOpen && orphans.length > 0 ? (
        <OrphanPanel
          orphans={orphans}
          currentVersion={version}
          onClose={() => setOrphanPanelOpen(false)}
        />
      ) : null}

      {noticeDismissed ? null : (
        <div
          role="status"
          data-state="takes_effect_next_login"
          data-testid="takes-effect-notice"
        >
          {t("admin.users.notice.takes_effect_next_login")}
          <button type="button" onClick={() => setNoticeDismissed(true)}>
            {t("admin.users.notice.dismiss")}
          </button>
        </div>
      )}

      <SyncRetryButton
        visible={syncStatus !== "ok"}
        refreshing={syncRefresh.isPending}
        onRefresh={onSyncRefresh}
      />
    </div>
  );
}

/**
 * classifyKindFromStatus — fallback used when the thrown error
 * does not carry a `kind` discriminator.
 */
function classifyKindFromStatus(
  status: number | undefined,
): UserRoleFetchError["kind"] {
  if (status === 403) return "error_permission_denied";
  if (status === 409) return "error_stale_version";
  return "error_fetch";
}
